import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Skeleton, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Chip, 
  TextField,
  MenuItem,
  Grid,
  Button,
  Alert
} from '@mui/material';
import { api } from '../../services/api';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import { formatDate } from '../../utils/formatters';

interface MarginPosition {
  id: number;
  margin: string;
  fromAssetCode: string;
  toAssetCode: string;
  credit: string;
  userId: number;
  username?: string;
  orderType: string;
  quantity: string;
  leverage: string;
  entryPrice: string;
  realizedPnl?: string;
  status: string;
  exitPrice?: string;
  positionCloseAmount?: string;
  interestAmountAccumulated: string;
  interestPercentAccumulated: string;
  lastCommissionUpdate?: string;
  createdAt: string;
  updatedAt: string;
}

interface MarginPositionFilters {
  status: string;
  userId: string;
  orderType: string;
  page: number;
  limit: number;
}

interface MarginPositionsResponse {
  data: MarginPosition[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const fetchMarginPositions = async (filters: MarginPositionFilters): Promise<MarginPositionsResponse> => {
  try {
    const params: any = {};
    
    if (filters.status) params.status = filters.status;
    if (filters.userId) params.userId = parseInt(filters.userId);
    if (filters.orderType) params.orderType = filters.orderType;
    
    params.limit = filters.limit;
    params.page = filters.page;

    const response = await api.get('/api/v1/admin-panel/margin/positions', {
      params,
      headers: {
        'x-hasura-admin-secret': localStorage.getItem('hasura_admin_secret') || 'admin_secret_key'
      }
    });

    return response.data.data;
  } catch (error) {
    console.error('Error fetching margin positions:', error);
    throw error;
  }
};

const MarginPositionsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [positions, setPositions] = useState<MarginPosition[]>([]);
  const [totalPositions, setTotalPositions] = useState(0);
  
  const [filters, setFilters] = useState<MarginPositionFilters>({
    status: '',
    userId: '',
    orderType: '',
    page: 1,
    limit: 50
  });

  const loadPositions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchMarginPositions(filters);
      
      if (response?.data && Array.isArray(response.data)) {
        setPositions(response.data);
        setTotalPositions(response.total || 0);
      } else {
        console.error('Invalid response structure:', response);
        setPositions([]);
        setTotalPositions(0);
        setError('Не удалось загрузить позиции: неверный формат данных');
      }
    } catch (err: any) {
      console.error('Error loading margin positions:', err);
      setPositions([]);
      setTotalPositions(0);
      setError(err.response?.data?.message || err.message || 'Ошибка при загрузке позиций');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPositions();
  }, [filters]);

  const handleFilterChange = (field: keyof MarginPositionFilters, value: string | number) => {
    setFilters(prev => ({ ...prev, [field]: value, page: 1 }));
  };

  const handleRefresh = () => {
    loadPositions();
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      userId: '',
      orderType: '',
      page: 1,
      limit: 50
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'primary';
      case 'executed':
        return 'success';
      case 'closed':
        return 'default';
      case 'liquidated':
        return 'error';
      default:
        return 'default';
    }
  };

  const getOrderTypeColor = (orderType: string) => {
    switch (orderType) {
      case 'long':
        return 'success';
      case 'short':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderFilters = () => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <FilterListIcon sx={{ mr: 1 }} />
        Фильтры
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Статус"
            select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            size="small"
          >
            <MenuItem value="">Все статусы</MenuItem>
            <MenuItem value="open">Открыта</MenuItem>
            <MenuItem value="closed">Закрыта</MenuItem>
            <MenuItem value="executed">Исполнена</MenuItem>
            <MenuItem value="liquidated">Ликвидирована</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="ID пользователя"
            value={filters.userId}
            onChange={(e) => handleFilterChange('userId', e.target.value)}
            size="small"
            type="number"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Тип позиции"
            select
            value={filters.orderType}
            onChange={(e) => handleFilterChange('orderType', e.target.value)}
            size="small"
          >
            <MenuItem value="">Все типы</MenuItem>
            <MenuItem value="long">LONG</MenuItem>
            <MenuItem value="short">SHORT</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={clearFilters} size="small">
              Очистить
            </Button>
            <Button variant="contained" onClick={handleRefresh} size="small" startIcon={<RefreshIcon />}>
              Обновить
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderPositionsTable = () => {
    if (loading) {
      return (
        <Box>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={60} sx={{ mb: 1 }} />
          ))}
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error">
          Ошибка при загрузке данных: {error}
        </Alert>
      );
    }

    if (!positions || positions.length === 0) {
      return (
        <Alert severity="info">
          Позиции не найдены
        </Alert>
      );
    }

    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Пользователь</TableCell>
            <TableCell>Пара</TableCell>
            <TableCell>Тип</TableCell>
            <TableCell>Количество</TableCell>
            <TableCell>Плечо</TableCell>
            <TableCell>Цена входа</TableCell>
            <TableCell>Цена выхода</TableCell>
            <TableCell>P&L</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell>Дата создания</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {positions.map((position) => (
            <TableRow key={position.id}>
              <TableCell>{position.id}</TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">ID: {position.userId}</Typography>
                  {position.username && (
                    <Typography variant="caption" color="text.secondary">
                      {position.username}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {position.fromAssetCode}/{position.toAssetCode}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip 
                  label={position.orderType === 'long' ? 'LONG' : 'SHORT'} 
                  color={getOrderTypeColor(position.orderType)} 
                  size="small" 
                />
              </TableCell>
              <TableCell>{parseFloat(position.quantity).toFixed(8)}</TableCell>
              <TableCell>{position.leverage}x</TableCell>
              <TableCell>{parseFloat(position.entryPrice).toFixed(8)}</TableCell>
              <TableCell>
                {position.exitPrice ? parseFloat(position.exitPrice).toFixed(8) : '—'}
              </TableCell>
              <TableCell>
                {position.realizedPnl ? (
                  <Typography 
                    variant="body2" 
                    color={parseFloat(position.realizedPnl) >= 0 ? 'success.main' : 'error.main'}
                  >
                    {parseFloat(position.realizedPnl) >= 0 ? '+' : ''}{parseFloat(position.realizedPnl).toFixed(2)}
                  </Typography>
                ) : '—'}
              </TableCell>
              <TableCell>
                <Chip 
                  label={position.status} 
                  color={getStatusColor(position.status)} 
                  size="small" 
                />
              </TableCell>
              <TableCell>{formatDate(position.createdAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Маржинальные позиции
      </Typography>
      
      {renderFilters()}
      
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Найдено позиций: {totalPositions}
        </Typography>
        {renderPositionsTable()}
      </Paper>
    </Box>
  );
};

export default MarginPositionsPage;